import { Store } from 'redux';
import Bugsnag from '@bugsnag/js';
import { createHttpLink } from '@apollo/client';
import React from 'react';
import './scss/index.scss';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { initBrowserApollo } from 'src/core/engine/browser/initBrowserApollo';
import { APP_ENV_STATE_ID } from 'src/core/config';
import { userRegister } from 'src/features/user/user.register';
import { cspRegister, CspSession } from 'src/components/CustomerServicePortal/feature/csp.register';
import { readJson } from 'src/core/engine/utils/readJson';
import { createCurrencyBrowser } from 'src/core/state/currency/browser';
import { cartRegister } from 'src/features/cart/cart.register';
import { HeadTagsProvider, HeadTagsInjector } from 'src/core/engine/head';
import { ErrorBoundary, reportReactError } from 'src/components/Bugsnag/BugsnagClient';
import { SmartlookBoundary } from 'src/components/Smartlook/SmartlookBoundary';
import { CookiesProvider } from 'src/util/cookies';
import { DEVICE_CLASS_ATTR, DeviceClassContext, parseDeviceClass } from 'src/core/engine/deviceClass';

import { appRegister } from './features/app/app.register';
import { initialState } from './features/app/app.reducer';
import { ErrorComponent } from './components/Error/ErrorComponent';
import { Outdated } from './components/Outdated/Outdated';
import { NotFound } from './components/NotFound/NotFound';
import { paths, text } from './config';
import urlResolver from './queries/urlResolver.graphql';
import { knownRoutes } from './routes';
import { apiUrl } from './util/apiUrl';
import { AppShell } from './components/AppShell/AppShell';
import { suppressGqlErrors } from './components/Banner/links/missing-cms.link';
import { outOfBoundsErrorLink } from './components/CategoryListingPage/links/productList.links';
import getCartQuery from './queries/getCart.graphql';
import { toNotifiableError } from './util/general';

const { link: currencyLink, register: registerCurrency, appendHeader: appendCurrencyHeader } = createCurrencyBrowser();

const { links, ...rest } = appRegister();

/**
 * In production, the env vars will come from the in-page JSON
 * but in development we can import the vars
 */
const appEnv = readJson(APP_ENV_STATE_ID);

const apiBase = location.origin + '/graphql';

let httpLink = createHttpLink({
    uri: apiBase,
    useGETForQueries: true,
});

/**
 * If we're in a testing environment, use a different
 * HTTP link to resolve GQL queries
 */
if (process.env.NODE_ENV === 'production') {
    /** noop **/
} else {
    if (window.Cypress) {
        httpLink = require('./features/cypress/http-link').createHttpLink({
            uri: apiBase,
            useGETForQueries: true,
        });
    }
}

let reduxStore: null | Store<any> = null;

const createApp = initBrowserApollo({
    unionAndInterfaceTypes: require('../__generated__/UNION_AND_INTERFACES.json'),
    urlResolver,
    env: appEnv,
    knownRoutes,
    deps: {
        apiUrl,
        paths,
        text,
        restHeaders: (incoming) => {
            const cspHeaders = {};
            try {
                if (reduxStore) {
                    const session = reduxStore.getState().csp?.session;
                    const isCsp = session === CspSession.SignedIn;
                    if (isCsp) {
                        cspHeaders['wwe-cs-portal'] = true;
                    }
                }
            } catch (e) {
                Bugsnag.notify(toNotifiableError(e));
            }
            const outgoing = {
                ...incoming,
                ...appendCurrencyHeader({}).headers,
                ...cspHeaders,
            };
            return outgoing;
        },
        queries: {
            getCart: getCartQuery,
        },
    },
    asyncLoader: (componentName: string) =>
        import(/* webpackChunkName: "[request]_root" */ `./RootComponents/${componentName}`),
    resolveWeakLoader: (componentName: string) => (require as any).resolveWeak(`./RootComponents/${componentName}`),
    features: [rest, userRegister(), cspRegister(), cartRegister(), registerCurrency()],
    links: [outOfBoundsErrorLink, suppressGqlErrors, currencyLink, ...links, httpLink],
    initialState: {
        app: {
            ...initialState,
            online: window.navigator.onLine,
            // renderEnv stays 'server' until hydration commits — see AppShell.
        },
    },
    components: {
        ErrorComponent: ErrorComponent,
        OfflineComponent: ErrorComponent,
        OutdatedComponent: Outdated,
        NotFoundComponent: NotFound,
    },
});

const { App, Async, store } = createApp();
reduxStore = store;

const rootElement = document.getElementById('root');
if (rootElement) {
    // The device class the server rendered for (see ssrApp.tsx); hydration must use the same one.
    const device = parseDeviceClass(rootElement.getAttribute(DEVICE_CLASS_ATTR));
    const appTree = (
        <DeviceClassContext.Provider value={device}>
            <ErrorBoundary>
                <CookiesProvider>
                    <App>
                        <SmartlookBoundary>
                            <HeadTagsProvider>
                                <HeadTagsInjector />
                                <AppShell>{Async}</AppShell>
                            </HeadTagsProvider>
                        </SmartlookBoundary>
                    </App>
                </CookiesProvider>
            </ErrorBoundary>
        </DeviceClassContext.Provider>
    );
    // Hydrate whenever the server sent markup; createRoot would wipe it.
    if (rootElement.hasChildNodes()) {
        hydrateRoot(rootElement, appTree, {
            onRecoverableError: (error, errorInfo) => {
                console.error(error);
                reportReactError(error, errorInfo.componentStack);
            },
        });
    } else {
        createRoot(rootElement).render(appTree);
    }
}
